import moment from 'moment'
import { RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { createSelector } from 'reselect'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import {
  findPostPurchaseCheckout,
  getConnectRoomsSpecialRequestMessage,
  isBedbankItem,
  isCarHireItem,
  isFlightItem,
  isInstantBookingLEHotelItem,
  getContactName,
  getPrimaryRoomTraveller,
  getAdditionalTravellerNames,
  getRoomLoyaltyProgramMemberId,
} from 'lib/checkout/checkoutUtils'
import { sum } from 'lib/array/arrayUtils'
import {
  CHECKOUT_ITEM_TYPE_FLIGHT,
  GIFT_RECIPIENT_FORM_ID,
  PRIMARY_TRAVELLER_FORM_ID,
} from 'constants/checkout'
import { ITEM_TYPE_BEDBANK } from 'constants/cart'
import config from 'constants/config'
import uuidV4 from 'lib/string/uuidV4Utils'
import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import { computeBaggageCostForPaxList } from 'checkout/lib/utils/flights/extras'
import { OFFER_DYNAMIC_PRICING_TYPES, OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import { buildBaggagePayload, buildPassengerPayload } from 'checkout/lib/utils/flights/order'
import { buildTravellerInfo } from 'checkout/lib/utils/experiences/order'
import { getJourneyV2IdKey } from 'lib/flights/flightUtils'
import { excludeNullOrUndefined } from 'checkout/utils'
import { Order } from '@luxuryescapes/contract-svc-order'
import { generateScheduleDate } from 'lib/payment/gift'
import { getInstantBookingPriceAndSurcharge } from 'checkout/lib/utils/accommodation/price'
import { getAge } from 'lib/datetime/dateUtils'
import { TRANSPORTATION_CODE } from 'components/CarHire/CarHireHardcodedLists'
import {
  checkoutAccommodationOfferView,
  getLeInstantBookingItems
} from 'checkout/selectors/view/accommodation'
import { getTravellerFormSchemaRequest, isTravellerFormNeeded } from 'checkout/selectors/request/travellerSchema'
import { baggageOptionsMap } from 'api/lib/convertJourneyV2'
import { getFullOffers } from 'selectors/offerSelectors'
import { getFlightBreakdownView } from 'checkout/selectors/view/flights'
import { getBedbankChangeDatesTravellers } from '../bedbankChangeDatesTravellers'
import { getCarHireSelectedRateOption } from 'checkout/lib/utils/carHire/view'
import { FlightViewTypes } from 'constants/flight'
import { getChannelMarkup } from 'selectors/channelMarkupSelector'
import { filterItemDiscounts, PromoItemDiscountsWithStatus } from 'lib/promo/promoMappers'
import { getAccommodationAndExperienceItemDiscounts, getAllPromoCodeItemDiscounts } from './checkout'
import getCheckoutTravellerSchema from 'checkout/selectors/getCheckoutTravellerSchema'
import formatItemDiscount from 'lib/payment/formatDiscountItem'
import { isLebtServiceFeesEnabled } from 'businessTraveller/selectors/businessTravellerServiceFeesSelectors'
import { CHAIN_NAME_TO_LOYALTY_PROGRAM } from 'lib/search/constants'

export const getFormattedTravellers = createSelector(
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => getCheckoutTravellerSchema(state),
  (travellerForms, schemaData) => {
    if (schemaData && travellerForms.length > 0) {
      return buildTravellerInfo(schemaData.accommodationFlightSchema, travellerForms)
    }
  },
)

const selectFormattedBusinessTravellerCreditItem = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getFlightBreakdownView(state),
  (
    accommodationOfferViews,
    flightBreakdownViewsWithStatus,
  ): App.BusinessTraveller.CreditOrderItem | undefined => {
    let total = 0
    for (const offerView of accommodationOfferViews.data) {
      offerView.itemViews.forEach((itemView: App.Checkout.AnyItemView) => {
        total += itemView.totals.businessTravellerCredits ?? 0
      })
    }

    for (const flightBreakdownView of flightBreakdownViewsWithStatus.data) {
      total += flightBreakdownView.businessTravellerCredits ?? 0
    }

    if (total === 0) {
      return undefined
    }

    return {
      type: 'business_credit',
      transaction_key: uuidV4(),
      total,
    }
  },
)

const selectBusinessTravellerServiceFeeCartItem = createSelector(
  (state: App.State) => state.checkout.cart.cartId,
  (state: App.State) => state.businessTraveller.serviceFees,
  (state: App.State) => isLebtServiceFeesEnabled(state),
  (
    cartId,
    serviceFees,
    lebtServiceFeesEnabled,
  ): App.BusinessTraveller.BusinessServiceFeeCartItem | undefined => {
    if (!lebtServiceFeesEnabled) {
      return undefined
    }

    const serviceFee = serviceFees[cartId]
    if (!serviceFee?.amount || !serviceFee.source || !serviceFee.type) {
      return undefined
    }

    return {
      type: 'service_fee',
      transaction_key: uuidV4(),
      serviceFeeType: 'fixed_amount', // fixed amount is needed for compatibility with the legacy payments service fee
      serviceFeeSource: serviceFee.source,
      serviceFeeChargeType: serviceFee.type,
      ...(serviceFee.destination ? { serviceFeeDestination: serviceFee.destination } : {}),
      ...(serviceFee.product ? { serviceFeeProduct: serviceFee.product } : {}),
      conciergeMode: serviceFee.conciergeMode ?? false,
      serviceFeeAmount: serviceFee.amount,
    }
  },
)

export function formatCartItems(
  cartState: App.CheckoutCartState,
  offers: Record<string, App.AnyOffer>,
  travellerForms: Array<App.Checkout.TravellerForm>,
  journeys: Record<string, App.AnyJourney>,
  isTravellerFormNeeded: boolean,
  formSchemaRequest: { items: Array<any> },
  formattedInstantBookingHotelItems: Array<unknown>,
  formattedBusinessTravellerCreditItem: App.BusinessTraveller.CreditOrderItem | undefined,
  formattedBusinessTravellerServiceFeeCartItems: App.BusinessTraveller.BusinessServiceFeeCartItem | undefined,
  existingOrderTraveller: App.ExistingOrderBedbankTraveller | undefined,
  promoCodeItemDiscounts: PromoItemDiscountsWithStatus,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
): App.WithDataStatus<Array<any>> {
  const { itemDiscounts: allItemDiscounts, hasRequiredPromoCodeData } = promoCodeItemDiscounts

  if (
    isTravellerFormNeeded &&
    formSchemaRequest.items.length > 0 &&
    !travellerForms.some(form => form.id === PRIMARY_TRAVELLER_FORM_ID) ||
    !promoCodeItemDiscounts.hasRequiredPromoCodeData
  ) {
    return { hasRequiredData: false, data: [] }
  }

  let hasRequiredData = true

  const { items, specialRequests, arrivalFlightNumber } = cartState

  const formattedCarHireItems = items
    .filter(isCarHireItem)
    .map((item) => formatCarHireItem(item, travellerForms, arrivalFlightNumber, allItemDiscounts))

  // Each bedbank item is now formatted as a separate order item.
  const formattedBedbankItems = items.filter(isBedbankItem).map(item => {
    const offer = offers[item.offerId] as App.BedbankOffer
    if (!offer) {
      hasRequiredData = false
      return null
    }
    const request = specialRequests?.[item.itemId] ?? ''
    const itemDiscounts = filterItemDiscounts({
      itemDiscounts: allItemDiscounts,
      lookup: { specificType: 'accommodation' },
    })

    return formatBedbankItem(item, request, travellerForms, itemDiscounts, offer, existingOrderTraveller)
  }).filter(Boolean)

  const formattedFlightItems = items
    .filter(isFlightItem)
    .reduce<Array<unknown>>((formattedItems, item) => {
      let flightJourneys: Array<App.JourneyV2> = []

      if (item.searchId) {
        const keys = item.flights.map(flight => getJourneyV2IdKey(flight.journeyId, item.searchId!, flight.fareFamily?.id))
        flightJourneys = keys.map(key => journeys[key] as App.JourneyV2)
      }

      if (!flightJourneys[0] || !item.hasFinalFare) {
        hasRequiredData = false
      }

      const data = flightJourneys[0] ? [
        ...formattedItems,
        ...formatFlightItem(item, travellerForms, flightJourneys, allItemDiscounts, schemaData),
      ] : formattedItems

      return data
    }, [])

  // Check if data requirements satisfied
  hasRequiredData = hasRequiredData &&
    hasRequiredPromoCodeData

  const formattedItems = [
    ...formattedInstantBookingHotelItems,
    ...formattedBedbankItems,
    ...formattedFlightItems,
    ...formattedCarHireItems,
  ]

  if (formattedBusinessTravellerCreditItem) {
    formattedItems.push(formattedBusinessTravellerCreditItem)
  }

  if (formattedBusinessTravellerServiceFeeCartItems) {
    formattedItems.push({ ...formattedBusinessTravellerServiceFeeCartItems, transaction_key: uuidV4() })
  }

  return {
    hasRequiredData,
    data: formattedItems,
  }
}

function formatLeHotelItem(
  item: App.Checkout.InstantBookingLEHotelItem | App.Checkout.BNBLLEHotelItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  specialRequest: string,
  offer: App.Offer,
  instantBookingPricing: (App.Checkout.ItemViewTotals & {
    extraGuestSurchargesPayableAtProperty?: boolean,
  }) | undefined,
  account: App.AuthAccount,
  connectedRoomOfferIds: Array<string>,
  cartMode?: App.CheckoutCartMode,
  arrivalDetails?: App.ArrivalDetails,
  allItemDiscounts?: Array<App.Checkout.ItemDiscount>,
  channelMarkup?: App.ChannelMarkup,
) {
  let pkgUniqueKey = getPackageUniqueKey(item.packageId, item.duration, item.roomRateId)
  if (item.hasLoyaltyBenefitRoomUpgrade) {
    const originPackage = offer.packages.find(pkg => pkg.uniqueKey === pkgUniqueKey)
    if (originPackage?.loyalty.targetUpgradePackageUniqueKey) {
      const upgradePackage = offer.packages.find(pkg => pkg.uniqueKey === originPackage.loyalty.targetUpgradePackageUniqueKey)
      if (upgradePackage) {
        // @ts-expect-error string satisfies the magic string here
        pkgUniqueKey = upgradePackage.uniqueKey
      }
    }
  }
  const pkg = offer.packages.find(pkg => pkg.uniqueKey === pkgUniqueKey)

  if (!pkg) {
    throw new Error(`Package with unique key ${pkgUniqueKey} not found in offer ${offer.id}`)
  }

  const priceFields = {
    price: instantBookingPricing?.price ?? pkg.price ?? 0,
    memberPrice: (instantBookingPricing?.memberPrice || pkg.memberPrice) ?? 0,
    taxesAndFees: instantBookingPricing?.taxesAndFees ?? pkg.taxesAndFees ?? 0,
    surcharge: instantBookingPricing?.surcharge ?? 0,
    extraGuestSurcharge: instantBookingPricing?.extraGuestSurcharge ?? 0,
    extraGuestSurchargesPayableAtProperty: instantBookingPricing?.extraGuestSurchargesPayableAtProperty ?? false,
  }
  const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts ?? [], lookup: { specificType: 'accommodationAndExperiences', itemIds: [item.itemId] } })

  const newBookingFields = {
    offer_id: offer.id,
    offer_type: offer.type,
    offer_name: offer.name,
    offer_slug: offer.slug,
    offer_booking_type: 'reservation',
    package_id: pkg.id,
    room_rate_id: pkg.roomRate?.id,
    package_name: pkg.name,
    number_of_nights: pkg.duration,
    value: pkg.value,
    localizedPrices: pkg.trackingPrice,
    price: priceFields.price,
    public_price: offer.type === OFFER_TYPE_ALWAYS_ON ? priceFields.price : undefined,
    lux_plus_price: offer.type === OFFER_TYPE_ALWAYS_ON && priceFields.memberPrice > 0 ? priceFields.memberPrice : undefined,
    surcharge: priceFields.surcharge,
    extra_guest_surcharge: priceFields.extraGuestSurchargesPayableAtProperty ? 0 : priceFields.extraGuestSurcharge,
    extra_guest_surcharge_payable_at_property: priceFields.extraGuestSurchargesPayableAtProperty ? priceFields.extraGuestSurcharge : 0,
    reservation_type: item.reservationType,
  }

  if (item.newPrice) {
    // accept a new price due to price change
    newBookingFields.price = item.newPrice
  }
  if (item.newSurcharge) {
    newBookingFields.surcharge = item.newSurcharge
  }
  if (item.newExtraGuestSurcharge) {
    newBookingFields.extra_guest_surcharge = item.newExtraGuestSurcharge
  }

  const selectDateFields = {
    ...(cartMode == 'select-date' && 'orderItemId' in item && {
      type: 'reservation',
      itemId: item.orderItemId,
      peak_period_surcharge: priceFields.surcharge,
      extra_guest_surcharge: priceFields.extraGuestSurcharge,
    }),
  }

  const arrivalDetailFields = {
    ...(arrivalDetails && {
      arrival_date: moment.utc(arrivalDetails.arrivalDate).format(ISO_DATE_FORMAT),
      arrival_time: arrivalDetails.arrivalTime,
      arrival_flight_number: arrivalDetails.arrivalFlightNumber,
    }),
  }

  // primaryTraveller will be absent on package upgrading
  const primaryRoomTraveller = item.reservationType === RESERVATION_TYPE_INSTANT_BOOKING
    ? getPrimaryRoomTraveller(item.occupancy, travellerForms)
    : undefined

  const primaryRoomTravellerId = primaryRoomTraveller?.employeeId ?? primaryRoomTraveller?.guestId

  const additionalTravellerNames = item.occupancy
    ? getAdditionalTravellerNames(item.occupancy, travellerForms, primaryRoomTravellerId)
    : []

  const connectedRoomRequest = connectedRoomOfferIds.includes(offer.id)
    ? getConnectRoomsSpecialRequestMessage(getContactName(primaryRoomTraveller, account))
    : ''

  const additionalTravellers = additionalTravellerNames.length > 0
    ? `Additional traveller: ${additionalTravellerNames.join(', ')}`
    : ''

  const guestSpecialRequests = [
    specialRequest,
    connectedRoomRequest,
    additionalTravellers,
  ]
    .filter(Boolean)
    .join('; ')

  const data: any = {
    // common fields
    cart_item_id: item.itemId,
    transaction_key: item.transactionKey,
    guest_special_requests: guestSpecialRequests,
    customer_email: primaryRoomTraveller?.data.email || account.email,
    customer_name: getContactName(primaryRoomTraveller, account),
    arrival_details: arrivalDetailFields,
    item_discounts: itemDiscounts.map(formatItemDiscount),
    ...(cartMode === 'select-date' ? selectDateFields : newBookingFields),
    // Used for business budget
    number_of_nights: pkg.duration,
    longitude: offer.property?.longitude,
    latitude: offer.property?.latitude,
  }

  if (item.reservationType === RESERVATION_TYPE_INSTANT_BOOKING) {
    data.check_in = moment.utc(item.checkIn).format(ISO_DATE_FORMAT)
    data.check_out = moment.utc(item.checkOut).format(ISO_DATE_FORMAT)
    data.number_of_adults = item.occupancy.adults
    data.number_of_children = item.occupancy.children
    data.number_of_infants = item.occupancy.infants
    data.children_ages = item.occupancy.childrenAge ?? []
    data.rate_plan_id = pkg.roomRate?.id
    data.is_upgraded_rate = item.isUpgradedRate

    if (primaryRoomTraveller) {
      data.guest_first_name = primaryRoomTraveller.data.firstName
      data.guest_last_name = primaryRoomTraveller.data.lastName
      data.guest_phone = (primaryRoomTraveller.data.phonePrefix && primaryRoomTraveller.data.phone) ? `${primaryRoomTraveller.data.phonePrefix}${primaryRoomTraveller.data.phone}` : undefined
    }

    if (OFFER_DYNAMIC_PRICING_TYPES.includes(offer.type)) {
      const instantBookingPrice = instantBookingPricing?.price ?? 0
      data.room_rate_amount = item.newPrice || instantBookingPrice
    }
  }

  if (channelMarkup && !channelMarkup.channelMarkupBlacklist.includes(offer.id)) {
    data.channel_markup_id = channelMarkup.channelMarkupId
  }

  return data
}

function formatCarHireItem(
  item: App.Checkout.CarHireItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  arrivalFlightNumber: string | null,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
) {
  const primaryDriver = travellerForms.find((form) => form.id === PRIMARY_TRAVELLER_FORM_ID)
  const offer = item.offer

  const selectedRateOption = getCarHireSelectedRateOption(item)

  const totalPrice = selectedRateOption.payNowAmount + (item.selectedInsurance?.total || 0)
  const payOnPickUpAmount = selectedRateOption.payOnArrivalAmount + sum(item.selectedAddons, addon => addon.total ?? 0)

  const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'car_hire' } })

  return {
    transaction_key: item.transactionKey,
    type: 'car_hire',
    offer,
    pickUpLocation: {
      code: offer.pickUp.id,
      date: moment.utc(`${item.pickUpDate}T${item.pickUpTime}`),
    },
    returnLocation: {
      code: offer.dropOff.id,
      date: moment.utc(`${item.dropOffDate}T${item.dropOffTime}`),
    },
    reference: {
      dateTime: selectedRateOption.dateTime,
      id: selectedRateOption.id,
      idContext: selectedRateOption.idContext,
      type: selectedRateOption.type,
      url: selectedRateOption.url,
    },
    vehicle: {
      code: offer.vehicle.code,
      model: offer.vehicle.model,
      image: offer.vehicle.image.url,
      transmissionType: offer.vehicle.transmission,
      baggageQuantity: offer.vehicle.luggage,
      passengerQuantity: offer.vehicle.seats,
      fuelType: offer.vehicle.fuelType,
      doorCount: offer.vehicle.doors,
      category: offer.vehicle.type,
      driveType: offer.vehicle.driveType,
      size: {
        code: offer.vehicle.sizeCode,
        description: offer.vehicle.type,
      },
      type: {
        code: offer.vehicle.typeCode,
        description: offer.vehicle.type,
      },
    },
    driver: {
      givenName: primaryDriver?.data.firstName,
      surname: primaryDriver?.data.lastName,
      email: primaryDriver?.data.email,
      phoneNumber: primaryDriver?.data.phone,
      phoneCode: primaryDriver?.data.phonePrefix,
      age: primaryDriver?.data.dateOfBirth ? getAge(primaryDriver.data.dateOfBirth, new Date(item.pickUpDate)) : undefined,
    },
    value: item.value,
    total: totalPrice,
    payOnPickUpAmount,
    originalPrice: selectedRateOption.originalPrice,
    qtyPassengers: 1, // TODO car hire remove mock
    availabilityUuid: offer.availabilityUuid,
    ...(arrivalFlightNumber && {
      arrivalDetails: {
        number: arrivalFlightNumber,
        transportationCode: TRANSPORTATION_CODE.AIRPLANE,
      },
    }),
    ...(item.selectedAddons && {
      specialEquipments: Object.values(item.selectedAddons),
    }),
    ...(item.selectedInsurance && item.selectedInsurance.type === 'enhanced' && {
      insuranceReference: {
        id: item.selectedInsurance.id,
        amount: item.selectedInsurance.total,
        currencyCode: item.selectedInsurance.currency,
        url: item.selectedInsurance.url,
      },
    }),
    item_discounts: itemDiscounts.map(formatItemDiscount),
    ...(item.bundledOrderId && {
      bundledOrderId: item.bundledOrderId,
    }),
  }
}

// Each bedbank item is now formatted as a separate order item.
function formatBedbankItem(
  item: App.Checkout.BedbankHotelItem,
  specialRequest: string,
  travellers: Array<App.Checkout.TravellerForm>,
  itemDiscounts: Array<App.Checkout.ItemDiscount>,
  offer: App.BedbankOffer,
  existingOrderTraveller?: App.ExistingOrderBedbankTraveller,
) {
  const primaryTraveller = travellers.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

  let travellerFormData = {
    bookingPhone: primaryTraveller?.data.phonePrefix && primaryTraveller?.data.phone && `+${primaryTraveller.data.phonePrefix}${primaryTraveller.data.phone}`,
    bookingEmail: primaryTraveller?.data.email,
    guestFirstName: primaryTraveller?.data.firstName,
    guestLastName: primaryTraveller?.data.lastName,
    hotelLoyaltyProgramMemberId: primaryTraveller?.data.hotelLoyaltyProgramMemberId,
  }

  if (existingOrderTraveller) {
    travellerFormData = {
      ...travellerFormData,
      ...existingOrderTraveller,
    }
  }

  // Fallback to use property name when property chain name is not mapped to any loyalty program name
  const loyaltyProgramId = CHAIN_NAME_TO_LOYALTY_PROGRAM[offer.property.chainName ?? ''] ?? offer.property.name

  // Get the room's primary traveller from occupancy
  const roomOccupants = item.occupancy
  const primaryRoomTravellerId = roomOccupants.employeeIds?.[0] ?? roomOccupants.guestIds?.[0]
  const primaryRoomTraveller = travellers.find(
    t => t.employeeId === primaryRoomTravellerId || t.guestId === primaryRoomTravellerId
  ) ?? primaryTraveller

  const additionalTravellerNames = getAdditionalTravellerNames(
    roomOccupants,
    travellers,
    primaryRoomTravellerId
  )
  const additionalTravellers = additionalTravellerNames.length > 0
    ? `Additional traveller: ${additionalTravellerNames.join(', ')}`
    : ''

  const roomSpecialRequests = [
    specialRequest,
    additionalTravellers,
  ]
    .filter(Boolean)
    .join('; ')

  const hotelLoyaltyProgramMemberId = getRoomLoyaltyProgramMemberId(
    roomOccupants,
    travellers,
    primaryRoomTravellerId
  )

  return {
    brand: config.BRAND,
    type: ITEM_TYPE_BEDBANK,
    cart_item_id: item.itemId,
    session_id: item.sessionId,
    bed_group_id: item.bedGroupId,
    property_id: item.offerId,
    room_type_id: item.roomId,
    room_rate_id: item.roomRateId,
    is_flight_bundle: item.isFlightBundle,
    transaction_key: item.transactionKey,
    booking_email: travellerFormData?.bookingEmail,
    booking_phone: travellerFormData?.bookingPhone,
    // Single room per item now
    rooms: [{
      guest_first_name: primaryRoomTraveller?.data.firstName ?? travellerFormData.guestFirstName,
      guest_last_name: primaryRoomTraveller?.data.lastName ?? travellerFormData.guestLastName,
      guest_special_requests: roomSpecialRequests,
      hotel_loyalty_program: loyaltyProgramId && hotelLoyaltyProgramMemberId ? {
        program_id: loyaltyProgramId,
        member_id: hotelLoyaltyProgramMemberId,
      } : undefined,
      occupancy: generateOccupancyStringByRoom(roomOccupants),
    }],
    item_discounts: itemDiscounts.map(formatItemDiscount),
    is_upgraded_rate: item.isUpgradedRate,
    // added to calculate the business budget
    longitude: offer.property.longitude,
    latitude: offer.property.latitude,
    number_of_nights: item.duration,
  }
}

function formatPassengers(
  travellerForms: Array<App.Checkout.TravellerForm>,
  flightItem: App.Checkout.FlightItem,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
) {
  if (travellerForms.length === 0 || !flightItem?.flights[0]) { return [] }
  const carrierCodes = flightItem.flights.map((f) => f.carrier)
  const paxList = flightItem.passengers

  const paxBaggageMap = flightItem.flights.map(flight => flight.extras.baggage)
  const primaryForm = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

  const result = paxList.map((pax: App.Checkout.FlightPassenger) => {
    const paxFormData = travellerForms.find(form => pax.id === form.id)
    if (!paxFormData) { return null }
    const paxSchema = schemaData?.accommodationFlightSchema.properties[pax.id]

    const allBaggage = paxBaggageMap.map(baggage => baggage[pax.id])
    const baggage = buildBaggagePayload(allBaggage[0], allBaggage[1])
    return buildPassengerPayload(pax, paxFormData, primaryForm, baggage, carrierCodes, paxSchema)
  }).filter(excludeNullOrUndefined)

  return result
}

function formatFlightItem(
  item: App.Checkout.FlightItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  journeys: Array<App.JourneyV2>,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
): Array<unknown> {
  const isReturn = journeys.length === 2
  const isSameProvider = isReturn && new Set(journeys.map(journey => journey.provider)).size === 1

  if (item.viewType === FlightViewTypes.TWO_ONE_WAYS_AND_RETURN || item.viewType === FlightViewTypes.MULTI_CITY || isSameProvider) {
    return formatReturnFlightItemV2(item, travellerForms, journeys, allItemDiscounts, schemaData)
  }

  return formatFlightItemV2(item, travellerForms, journeys, allItemDiscounts, schemaData)
}

function formatFlightItemV2(
  item: App.Checkout.FlightItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  journeys: Array<App.JourneyV2>,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
) {
  if (travellerForms.length === 0) { return [] }
  const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

  const result = item.flights.map((flight, index) => {
    const segmentJourney = journeys[index]

    if (segmentJourney) {
      const baggageOptions = baggageOptionsMap(segmentJourney.flightGroup.flights[0])?.baggage ?? []

      const cost = sum([
        segmentJourney.price.all.totalFare,
        sum(Object.values(item?.otherFees ?? [])),
        computeBaggageCostForPaxList(item.passengers, baggageOptions, flight.extras.baggage),
      ])

      const tax = segmentJourney.price.all.totalTax

      const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'flight' } })

      return {
        provider: segmentJourney.provider,
        transaction_key: item.transactionKey,
        type: CHECKOUT_ITEM_TYPE_FLIGHT,
        offer_type: [(item.bundledItemIds?.length ?? 0) > 0 ? 'hotel' : null],
        contact_email_address: primaryTraveller?.data.email,
        contact_phone_number: primaryTraveller ? `${primaryTraveller?.data.phonePrefix}${primaryTraveller?.data.phone}` : undefined,
        passengers: formatPassengers(travellerForms, item, schemaData),
        journey: {
          id: flight.journeyId,
          cost,
          tax,
          searchId: item.searchId,
        },
        viewType: item.viewType,
        item_discounts: itemDiscounts.map(formatItemDiscount),
        // Used for the business budget
        departure_airport_code: item.originAirportCode,
        arrival_airport_code: item.destinationAirportCode,
      }
    }
  }).filter(excludeNullOrUndefined)

  return result
}

function formatReturnFlightItemV2(
  item: App.Checkout.FlightItem,
  travellerForms: Array<App.Checkout.TravellerForm>,
  journeys: Array<App.JourneyV2>,
  allItemDiscounts: Array<App.Checkout.ItemDiscount>,
  schemaData: App.Checkout.TravellerFormSchemaData | null,
) {
  if (travellerForms.length === 0) { return [] }
  const primaryTraveller = travellerForms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID)

  const cost = sum([
    ...item.flights.map(flight => flight.cost),
    sum(Object.values(item?.otherFees ?? [])),
    ...journeys.map((journey, index) => {
      const baggageOptions = baggageOptionsMap(journey.flightGroup.flights[0])?.baggage ?? []
      return computeBaggageCostForPaxList(item.passengers, baggageOptions, item.flights[index]?.extras.baggage ?? {})
    }),
  ])

  const tax = sum(journeys.map(journey => journey.price.all.totalTax))

  const itemDiscounts = filterItemDiscounts({ itemDiscounts: allItemDiscounts, lookup: { specificType: 'flight' } })

  const result = {
    provider: journeys[0].provider,
    transaction_key: item.transactionKey,
    type: CHECKOUT_ITEM_TYPE_FLIGHT,
    offer_type: [(item.bundledItemIds?.length ?? 0) ? 'hotel' : null],
    contact_email_address: primaryTraveller?.data.email,
    contact_phone_number: primaryTraveller ? `${primaryTraveller.data.phonePrefix}${primaryTraveller.data.phone}` : undefined,
    passengers: formatPassengers(travellerForms, item, schemaData),
    journey: {
      id: journeys[0].id,
      cost,
      tax,
      searchId: item.searchId,
      selectedFareIds: item.flights.map(flight => flight.journeyId),
    },
    viewType: item.viewType === FlightViewTypes.MULTI_CITY ?
      FlightViewTypes.MULTI_CITY :
      FlightViewTypes.TWO_ONE_WAYS_AND_RETURN,
    item_discounts: itemDiscounts.map(formatItemDiscount),
    // Used for the business budget
    departure_airport_code: item.originAirportCode,
    arrival_airport_code: item.destinationAirportCode,
  }

  return [result]
}

const getFormattedLeInstantBookingHotelItems = createSelector(
  (state: App.State) => getLeInstantBookingItems(state),
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.calendar.calendarsByOccupancy,
  (state: App.State) => state.offer.offerAvailableRatesByOccupancy,
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => state.checkout.cart.specialRequests,
  (state: App.State) => state.auth.account,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => getAccommodationAndExperienceItemDiscounts(state),
  (state: App.State) => getChannelMarkup(state),
  (state: App.State) => state.checkout.cart.connectedRoomOfferIds,
  (
    items,
    offers,
    calendarsRatesByOccupancy,
    availableRatesByOccupancy,
    travellerForms,
    specialRequests,
    account,
    cartMode,
    promoCodeItemDiscounts,
    channelMarkup,
    connectedRoomOfferIds = [],
  ) => {
    return items
      .filter(isInstantBookingLEHotelItem)
      .map((item) => {
        const offer = offers[item.offerId]
        if (!offer) {
          return null
        }

        const {
          data: instantBookingPricing,
          hasRequiredData,
        } = getInstantBookingPriceAndSurcharge(item, offer, calendarsRatesByOccupancy, availableRatesByOccupancy)

        const { itemDiscounts } = promoCodeItemDiscounts
        if (!hasRequiredData) {
          return null
        }
        const specificItemDiscounts = filterItemDiscounts({ itemDiscounts, lookup: { specificType: 'accommodation' } })

        return formatLeHotelItem(
          item,
          travellerForms,
          specialRequests[item.itemId],
          offer,
          instantBookingPricing,
          account,
          connectedRoomOfferIds,
          cartMode,
          undefined,
          specificItemDiscounts,
          channelMarkup
        )
      })
  },
)

export const getFormattedCartItems = createSelector(
  (state: App.State) => state.checkout.cart,
  (state: App.State) => getFullOffers(state),
  (state: App.State) => state.checkout.form.travellerForms,
  (state: App.State) => state.flights.journeysById,
  (state: App.State) => isTravellerFormNeeded(state),
  (state: App.State) => getTravellerFormSchemaRequest(state),
  (state: App.State) => getFormattedLeInstantBookingHotelItems(state),
  (state: App.State) => selectFormattedBusinessTravellerCreditItem(state),
  (state: App.State) => selectBusinessTravellerServiceFeeCartItem(state),
  (state: App.State) => getBedbankChangeDatesTravellers(state),
  (state: App.State) => getAllPromoCodeItemDiscounts(state),
  (state: App.State) => getCheckoutTravellerSchema(state),
  formatCartItems,
)

export const getOrderGift = createSelector(
  (state: App.State) => state.checkout.form.travellerForms,
  (travellers): Order.OrderGift | undefined => {
    if (travellers.length < 2) { return undefined }
    const giftRecipient = travellers.find(traveller => traveller.id === GIFT_RECIPIENT_FORM_ID)
    if (giftRecipient) {
      const giftGiver = travellers.find(traveller => traveller.id === PRIMARY_TRAVELLER_FORM_ID)
      const giftPayload: Order.OrderGift = {
        receiver_email: giftRecipient.data.email!,
        receiver_name: giftRecipient ? `${giftRecipient.data.firstName} ${giftRecipient.data.lastName}` : 'undefined',
        giver_name: giftGiver ? `${giftGiver.data.firstName} ${giftGiver.data.lastName}` : '',
        scheduled_date: generateScheduleDate(giftRecipient.data.scheduledDate!),
      }
      if (giftRecipient.data.personalMessage) {
        giftPayload.gift_message = giftRecipient.data.personalMessage
      }
      return giftPayload
    }
  },
)
